import { useCallback, useContext, useMemo } from 'react';
import { useStore } from 'zustand';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { ExtranetExperienceStatus } from 'app/typings/core/extranet/hotel/experiences';
import type { ExtranetHotelExperience } from 'app/typings/core/extranet/hotel/experiences';
import { isPackVisibleInExtranet } from 'app/utils/extranet';

import { inventoryStore } from '../../../../InventoryStore';
import { ExperienceInventoryType } from '../../../Header/ActionsBar/InventoryToggle/InventoryToggle';

const isTimeSlotExperience = (experience: ExtranetHotelExperience) =>
  experience.availabilityMode === AVAILABILITY_MODE.TIMESLOT &&
  !!experience.calendarId;

export const useFilteredExperiences = () => {
  const { experiencesList, packsList } = useContext(ModernHotelContext);
  const { data: experiencesCategories, isFetching } =
    useExperiencesCategories();
  const experienceInventoryType = useStore(
    inventoryStore,
    (state) => state.experienceInventoryType
  );

  const publishedPackExperienceIds = useMemo(() => {
    return new Set(
      packsList
        .filter((pack) => isPackVisibleInExtranet(pack.status))
        .flatMap((pack) =>
          pack.packExperiences.map((packExp) => packExp.experienceId)
        )
    );
  }, [packsList]);

  const categoriesMap = useMemo(() => {
    return new Map(
      experiencesCategories?.map((cat) => [cat.id, cat.name]) || []
    );
  }, [experiencesCategories]);

  const isInfiniteExperience = useCallback(
    (expId: number) => {
      const experience = experiencesList.find((exp) => exp.id === expId);
      const stockConfiguration =
        experience?.openingConfiguration?.stockConfiguration;

      const isConstantWithNullStock =
        stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.CONSTANT &&
        (stockConfiguration.configuration?.stock === null ||
          stockConfiguration.configuration?.stock === undefined);

      const isDailyWithAllDaysUnlimited =
        stockConfiguration?.type === OPENING_CONFIGURATION_TYPE.DAILY &&
        Object.values(stockConfiguration.days).every(
          (dayConfig) =>
            !dayConfig.isClosed &&
            (dayConfig.stock === null || dayConfig.stock === undefined)
        );

      return (
        stockConfiguration !== undefined &&
        (isConstantWithNullStock || isDailyWithAllDaysUnlimited)
      );
    },
    [experiencesList]
  );

  const displayableExperiences = useMemo(
    () =>
      experiencesList.filter(
        (experience) =>
          experience.status === ExtranetExperienceStatus.PUBLISHED &&
          (publishedPackExperienceIds.has(experience.id) ||
            experience.isAlwaysIncludedWithNight) &&
          experience.showInInventory
      ),
    [experiencesList, publishedPackExperienceIds]
  );

  const hasDailyStockExperiences = useMemo(
    () => displayableExperiences.some((exp) => !isTimeSlotExperience(exp)),
    [displayableExperiences]
  );

  const hasTimeSlotExperiences = useMemo(
    () => displayableExperiences.some(isTimeSlotExperience),
    [displayableExperiences]
  );

  const matchesInventoryType = useCallback(
    (experience: ExtranetHotelExperience) =>
      experienceInventoryType === ExperienceInventoryType.TIME_SLOTS
        ? isTimeSlotExperience(experience)
        : !isTimeSlotExperience(experience),
    [experienceInventoryType]
  );

  const sortedExperiences = useMemo(() => {
    const sorted = displayableExperiences
      .filter((experience) => matchesInventoryType(experience))
      .map((experience) => ({
        experience,
        categoryName: categoriesMap.get(experience.categoryId) || '',
        isAlwaysIncludedWithNight: experience.isAlwaysIncludedWithNight,
      }))
      .sort((a, b) => {
        return a.categoryName.localeCompare(b.categoryName, 'fr', {
          sensitivity: 'base',
        });
      });

    const sortedExps = sorted.reduce(
      (acc, item) => {
        if (item.isAlwaysIncludedWithNight) {
          acc.alwaysIncluded.push(item.experience);
        } else {
          acc.regular.push(item.experience);
        }

        return acc;
      },
      {
        regular: [] as ExtranetHotelExperience[],
        alwaysIncluded: [] as ExtranetHotelExperience[],
      }
    );

    return {
      ...sortedExps,
      alwaysIncluded: sortedExps.alwaysIncluded.sort(
        (a, b) => (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
      ),
    };
  }, [displayableExperiences, categoriesMap, matchesInventoryType]);

  return {
    sortedExperiences: sortedExperiences.regular,
    sortedExperiencesIsAlwaysIncludedWithNight:
      sortedExperiences.alwaysIncluded,
    experiencesCategories,
    isInfiniteExperience,
    isFetching,
    hasDailyStockExperiences,
    hasTimeSlotExperiences,
  };
};
